/**
 * shared/voice/keys.ts — every identifier in this product, and the rules that make them safe.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * audiobook's spine is *the sentence id is the only coordinate system*. This product's is narrower:
 * **a voice is `(voiceId, voiceRev)`, and a rev is the sha256 of the exact bytes we handed the GPU.**
 * Nothing here is addressed by a name, a label, or a host. `karti@spark1` and `karti@metal` are
 * different voices that happen to share a string (§2.2), and the fleet must be able to tell them
 * apart six weeks later when somebody says *"that doesn't sound like Ma."*
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── Why there is no `sha256()` in this file ─────────────────────────────────────────────────────
 *
 * VOX-LAW 6 makes a rev a hash, and hashing needs `node:crypto` — which `shared/` may not import,
 * because the client bundles this tree (§4.7; `test/wiring.test.ts` asserts the identifier `Buffer`
 * appears nowhere under `shared/`). WebCrypto's `crypto.subtle.digest` is global in both runtimes but
 * is async, which would make `voiceRev()` a promise at every call site including the client's.
 *
 * So the split is: **the caller hashes, this file formats and validates.** `voiceRev(hex)` takes a
 * 64-character lowercase digest and returns `"sha256:…"`; it throws on anything that is not one. The
 * bytes are on amd-server, the hash is computed there beside them by the worker, and the browser
 * never needs to compute one at all. A formatter that refuses malformed input is worth more here than
 * a hasher would be: the failure this prevents is a rev that was typed, guessed, or derived from a
 * label, which is precisely what VOX-LAW 6 exists to make impossible.
 *
 * ── Entropy is injected for the same reason ─────────────────────────────────────────────────────
 *
 * `mintId(prefix, bytes)` takes the random bytes. Both runtimes have `crypto.getRandomValues`, so
 * every caller is one line from a correct id, and `Math.random()` is not reachable by accident. On
 * the server that line is `randomBytes(n)`; in the browser it is `crypto.getRandomValues(new
 * Uint8Array(n))`. **Do not add a default argument that reaches for a global** — a default is how the
 * one code path that runs somewhere neither global exists gets a predictable id.
 */

// ─── id prefixes ─────────────────────────────────────────────────────────────

/**
 * The four minted ids, in the order the product creates them: a session is opened, it accumulates
 * recordings, the recordings yield candidate windows, and one candidate becomes the voice the family
 * takes home.
 *
 * ⚠️ **`tk_` is a recording, not a token.** In an audio studio "tk" is a take — the slate says
 * *scene 5, take 3* — and this whole design is written in studio vocabulary. The durable handle that
 * travels in a URL is `vox_`, below. Getting these two the wrong way round produces ids that parse,
 * validate and address the wrong table, so the mapping is written out per-prefix and
 * `test/wiring.test.ts` asserts each table's primary key against it.
 *
 * ⚠️ **`voices.id` has no prefix at all.** A voiceId is `"mom@vox"` (§2.1) — a fleet address, stable
 * forever, one per person per `seedKind` — and it is minted by `voiceId()` below, not by `mintId()`.
 * The reason it is not opaque is that it appears in the fleet manifest, in audiobook's config and in
 * a human conversation about whose voice is wrong; an opaque id there means nobody can answer that
 * conversation without a database.
 */
export const ID_PREFIX = {
  /** `voice_sessions.id` — one sitting in one room (§13). */
  session: "ses_",
  /** `takes.id` — one recorded attempt at one prompt. */
  take: "tk_",
  /** `candidates.id` — a window inside a take that could become a seed (§6.1). */
  candidate: "cnd_",
  /**
   * `voice_sessions.claimToken` — VOX-LAW 20's durable handle, the thing shown on screen in large
   * type with a copy button and printed into the notification that fires to Karti. It is what
   * `GET /c/:claimToken` resolves, so it travels through email, screenshots and retyping.
   */
  claim: "vox_",
} as const;

export type IdKind = keyof typeof ID_PREFIX;

/**
 * 16 bytes for an id, 24 for a claim token, and the difference is the whole reason both numbers are
 * here rather than one being a default.
 *
 * An id is opaque but not a capability — a session id is useless without either the cookie that owns
 * it or the claim token, so 16 bytes (~83 bits at base36) is ample against enumeration.
 *
 * A claim token IS the capability. It is the only thing standing between a stranger and a recording
 * of somebody's mother, it is deliberately long-lived (VOX-LAW 26: an unclaimed session is retained
 * indefinitely and no sweep may touch it), and it travels through channels that leak — email,
 * screenshots, a phone handed back. 24 bytes is ~124 bits at base36. audiobook's share token is the
 * same 24 bytes for the weaker case of a public-ish book.
 */
export const ID_BYTES = 16;
export const CLAIM_TOKEN_BYTES = 24;

/** How many base36 characters `ID_BYTES`/`CLAIM_TOKEN_BYTES` of entropy are rendered as. */
export const ID_CHARS = 25;
export const CLAIM_TOKEN_CHARS = 38;

export class KeyError extends Error {
  override name = "KeyError";
}

/**
 * `mintId("session", randomBytes(16))` → `"ses_9f3ck2…"`.
 *
 * Base36 over the whole byte string via BigInt, then left-padded — not per-byte `toString(36)`, which
 * is the obvious implementation and is biased: `255..toString(36)` is two characters and
 * `5..toString(36)` is one, so concatenating them makes short bytes under-represented and makes the
 * output length depend on the input's value. A biased id is not a security hole at 83 bits; a
 * variable-length one is a parsing bug waiting for the day somebody slices a fixed prefix off it.
 */
export function mintId(kind: IdKind, bytes: Uint8Array): string {
  const want = kind === "claim" ? CLAIM_TOKEN_BYTES : ID_BYTES;
  if (bytes.length < want) {
    throw new KeyError(`mintId(${kind}): needs ${want} bytes of entropy, got ${bytes.length}`);
  }
  const chars = kind === "claim" ? CLAIM_TOKEN_CHARS : ID_CHARS;
  let n = 0n;
  for (let i = 0; i < want; i++) n = (n << 8n) | BigInt(bytes[i]!);
  return `${ID_PREFIX[kind]}${n.toString(36).padStart(chars, "0").slice(-chars)}`;
}

const ID_BODY = /^[0-9a-z]+$/;

/** Shape check only. It says "this could be one of ours"; it never says the row exists. */
export function isId(kind: IdKind, s: unknown): s is string {
  if (typeof s !== "string") return false;
  const prefix = ID_PREFIX[kind];
  if (!s.startsWith(prefix)) return false;
  const body = s.slice(prefix.length);
  const want = kind === "claim" ? CLAIM_TOKEN_CHARS : ID_CHARS;
  return body.length === want && ID_BODY.test(body);
}

/**
 * Which kind an id is, from its prefix alone. `null` for anything that is not one of our four.
 *
 * Used at route boundaries so a take id arriving where a session id belongs is a 404 rather than a
 * lookup that happens to miss. Deliberately does not validate the body — that is `isId`'s job, and
 * conflating "which table" with "well formed" is how one caller's laxness becomes another's.
 */
export function idKindOf(s: string): IdKind | null {
  for (const [kind, prefix] of Object.entries(ID_PREFIX)) {
    if (s.startsWith(prefix)) return kind as IdKind;
  }
  return null;
}

// ─── the voice address ───────────────────────────────────────────────────────

/**
 * The register a seed was recorded in, and it is a first-class field rather than a variant (§2.1).
 *
 * The T3 conditioning prefix is a *style prior* — the model continues the register of the seed
 * (§5.2) — so a seed read in narration register makes the model narrate. That is exactly right for
 * audiobook and books, which are the flagship consumers, and exactly wrong for movies and music. Two
 * seeds per person means TWO `voiceId`s and two remote ids, decided now while it costs one column;
 * retrofitting a second register after 23 books are narrated will not happen.
 *
 * v1 ships `read` only. `talk` is the second button on the voice page (P5).
 */
export type SeedKind = "read" | "talk";
export const SEED_KINDS: readonly SeedKind[] = ["read", "talk"];

/** The site's namespace inside a fleet-wide voice address. audiobook's hosts use `@spark1`/`@metal`. */
export const VOICE_NAMESPACE = "vox";

/** `#`. Separates the address from the rev inside a `voiceKey`. Consumers never parse across it. */
export const VOICE_KEY_SEP = "#";

/**
 * A person's name as they typed it → the stable slug half of their voiceId.
 *
 * This runs exactly once per voice, on the one piece of metadata a family ever types (§3.1), and its
 * output is stable forever — so it is deliberately lossy in the safe direction and refuses rather
 * than guesses. Non-ASCII collapses to nothing rather than to a transliteration: a wrong
 * transliteration of a Gujarati name would become that person's permanent fleet address, and
 * `label` already carries the name exactly as she typed it for every screen that shows it.
 *
 * ⚠️ The slug is NOT unique by construction. Two people called Ma in one family is a real case, and
 * the caller resolves it against `voices` (`ma`, `ma2`, …) before minting. A slugger that silently
 * returned a colliding address would overwrite somebody's mother.
 */
export function slugifyName(name: string): string {
  const slug = name
    .normalize("NFKD")
    .toLowerCase()
    // The combining marks NFKD just split off, written as an escape rather than as literal combining
    // characters — a literal here renders as a mark stuck to the bracket in every editor and diff,
    // and audiobook's tree has already lost a phase to invisible code points in source.
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
  if (slug === "") {
    throw new KeyError(
      `slugifyName(${JSON.stringify(name)}): produced nothing addressable. ` +
        `The caller must ask again rather than mint a voiceId nobody can read.`,
    );
  }
  return slug;
}

/** `voiceId("mom")` → `"mom@vox"`. Stable forever, one per person per seedKind (§2.1). */
export function voiceId(slug: string): string {
  if (!/^[a-z0-9][a-z0-9-]{0,23}$/.test(slug)) {
    throw new KeyError(`voiceId(${JSON.stringify(slug)}): expected a slug from slugifyName()`);
  }
  return `${slug}@${VOICE_NAMESPACE}`;
}

export function parseVoiceId(id: string): { slug: string; namespace: string } {
  const at = id.indexOf("@");
  if (at <= 0 || at === id.length - 1) {
    throw new KeyError(`parseVoiceId(${JSON.stringify(id)}): expected "<slug>@<namespace>"`);
  }
  return { slug: id.slice(0, at), namespace: id.slice(at + 1) };
}

// ─── the rev ─────────────────────────────────────────────────────────────────

const SHA256_HEX = /^[0-9a-f]{64}$/;
export const REV_ALGO = "sha256";

/**
 * **VOX-LAW 6. `voiceRev` is `sha256` of the exact bytes POSTed to `/voices`. It is never typed by a
 * human, never derived from a label, and never equal to anything a human could guess.**
 *
 * A voice's identity is what we handed the model, not what we called it. Two things follow and both
 * have bitten this fleet's neighbours:
 *
 *  - The bytes hashed are the **delivered** bytes, after windowing, after the 16-bit write, after the
 *    −3 dBFS peak normalise (VOX-LAW 31) — not the take, not the candidate, not the seed "before the
 *    last step". VOX-LAW 25's corollary is the sharp edge here: if soxr or ffmpeg ever touches those
 *    bytes, its version is part of what produced the rev and belongs in the envelope, because a
 *    dependency bump would otherwise arrive inside audiobook's GPU cache key through the back door.
 *  - This is NOT audiobook's `voiceRev`. Theirs is a **`number`** (`shared/narration/keys.ts:83`),
 *    stored `voices.rev INTEGER NOT NULL DEFAULT 1`, mixed into `clipKey`, bumped by a human behind a
 *    `--yes` that prints how many clips it stales and how many GPU-hours that costs. Ours is a hash
 *    and it must never reach that column. §9.2: the manifest publishes an opaque `voiceKey` and
 *    writes *proposals*; consumers keep their integer.
 */
export function voiceRev(sha256Hex: string): string {
  const hex = sha256Hex.toLowerCase();
  if (!SHA256_HEX.test(hex)) {
    throw new KeyError(
      `voiceRev(): expected 64 lowercase hex characters of sha256 over the DELIVERED bytes, ` +
        `got ${JSON.stringify(sha256Hex).slice(0, 80)}. VOX-LAW 6: a rev is never typed and never ` +
        `derived from a label.`,
    );
  }
  return `${REV_ALGO}:${hex}`;
}

export const isVoiceRev = (s: unknown): s is string =>
  typeof s === "string" && s.startsWith(`${REV_ALGO}:`) && SHA256_HEX.test(s.slice(REV_ALGO.length + 1));

/** The hex digest back out, for a filename or a log line. Throws rather than returning a slice. */
export function revDigest(rev: string): string {
  if (!isVoiceRev(rev)) throw new KeyError(`revDigest(${JSON.stringify(rev)}): not a voiceRev`);
  return rev.slice(REV_ALGO.length + 1);
}

/** First 12 hex characters — for `/admin` and log lines only. Never an identifier, never a key. */
export const shortRev = (rev: string): string => revDigest(rev).slice(0, 12);

// ─── the fleet key ───────────────────────────────────────────────────────────

/**
 * `voiceKey("mom@vox", "sha256:9f3c…")` → `"mom@vox#9f3c…"` — what the fleet manifest publishes.
 *
 * **VOX-LAW 24. It is OPAQUE to consumers. They store it; they never parse it.** That is the whole
 * point of publishing a key rather than the pair: a consumer that parses `#` has taken a dependency
 * on our format, and the next thing that happens is a consumer deriving a cache key from the half
 * after the `#` — which is a hash, which changes on every retune, which stales every clip in
 * audiobook's library and re-enters a queue whose lane concurrency is 1 (§9.1).
 *
 * `parseVoiceKey` exists below because THIS repo has to read its own keys. It is exported so
 * `test/manifest-signature.test.ts` can round-trip; it is not part of the fleet contract, and a
 * consumer importing it has imported the wrong thing.
 */
export function voiceKey(id: string, rev: string): string {
  parseVoiceId(id);
  if (!isVoiceRev(rev)) throw new KeyError(`voiceKey(): second argument is not a voiceRev`);
  return `${id}${VOICE_KEY_SEP}${revDigest(rev)}`;
}

/** THIS REPO ONLY. See the note on `voiceKey` — a fleet consumer that calls this has a bug. */
export function parseVoiceKey(key: string): { voiceId: string; rev: string } {
  const at = key.indexOf(VOICE_KEY_SEP);
  if (at <= 0) throw new KeyError(`parseVoiceKey(${JSON.stringify(key)}): no ${VOICE_KEY_SEP}`);
  const id = key.slice(0, at);
  parseVoiceId(id);
  return { voiceId: id, rev: voiceRev(key.slice(at + 1)) };
}

// ─── the remote id ───────────────────────────────────────────────────────────

const REMOTE_ID = /^([a-z0-9][a-z0-9-]*)-r(\d+)$/;

/**
 * **VOX-LAW 7. A remote voice id is minted per rev and never reused.** `mom-r3` and `mom-r4` are
 * different remote voices on the same host, forever, even if `mom-r3` is retired.
 *
 * The ordinal is monotonic over **everything that has ever existed**, not over what exists now, and
 * the argument name says so. Deriving the next id from `GET /voices` — the obvious implementation —
 * is the bug §8.3 is about: that listing returns `{id,label,custom}` with no hash, no version and no
 * created-at, and reports `custom:false` even for a real clone, so it cannot distinguish "mom-r3 is
 * our bytes" from "mom-r3 is a name that survived a KUDA restart pointing at something else". Reusing
 * an ordinal after a retirement would silently narrate 23 books in whatever that id happens to hold.
 *
 * `voice_revs` is the source of truth for the high-water mark, because it is append-only.
 */
export function remoteId(slug: string, ordinal: number): string {
  if (!Number.isInteger(ordinal) || ordinal < 1) {
    throw new KeyError(`remoteId(${slug}, ${ordinal}): ordinal must be an integer >= 1`);
  }
  if (!/^[a-z0-9][a-z0-9-]{0,23}$/.test(slug)) {
    throw new KeyError(`remoteId(${JSON.stringify(slug)}): expected a slug from slugifyName()`);
  }
  return `${slug}-r${ordinal}`;
}

/** `nextRemoteId("mom", 3)` → `"mom-r4"`. The argument is the highest ordinal EVER minted. */
export const nextRemoteId = (slug: string, highestEverOrdinal: number): string =>
  remoteId(slug, highestEverOrdinal + 1);

export function parseRemoteId(id: string): { slug: string; ordinal: number } {
  const m = REMOTE_ID.exec(id);
  if (!m) throw new KeyError(`parseRemoteId(${JSON.stringify(id)}): expected "<slug>-r<n>"`);
  return { slug: m[1]!, ordinal: Number(m[2]) };
}

// ─── blobs ───────────────────────────────────────────────────────────────────

/**
 * What the origin on amd-server (:8962) stores, and the only six kinds there are.
 *
 * **VOX-LAW 19. There is no `GET /blob/:sha`.** Blob shas are never addressable from outside; every
 * byte route goes through a resource route that calls `readAccess` and refuses 404 (VOX-LAW 18). This
 * type exists so a new kind is a compile error in the origin's switch rather than a directory that
 * quietly appears under `/mnt/storage/voice/`.
 */
export type BlobKind = "take" | "seed" | "preview" | "consent" | "manifest" | "trial" | "quarantine" | "prompt";
export const BLOB_KINDS: readonly BlobKind[] = [
  "take",
  "seed",
  "preview",
  "consent",
  "manifest",
  "trial",
  /** Admin-imported source audio. No voice/seed row can reference this kind. */
  "quarantine",
  /** Human-authored examples played before a prompt; never synthesized. */
  "prompt",
];

/**
 * `take/9f/3c/9f3c….wav` — two levels of fan-out from the sha, then the whole sha.
 *
 * Two levels because one directory of 100k files makes `ls` on a struggling box a minute-long
 * operation during the exact incident you are trying to diagnose, and three levels is a directory
 * tree deeper than the fan-out saves. The sha is repeated in the filename so a file found by `find`
 * is self-describing — a content-addressed store whose leaves are named `data` cannot be verified
 * without its index.
 */
export function blobPath(kind: BlobKind, sha256Hex: string, ext: string): string {
  const hex = sha256Hex.toLowerCase();
  if (!SHA256_HEX.test(hex)) throw new KeyError(`blobPath(${kind}): not a sha256 hex digest`);
  if (!/^[a-z0-9]{1,8}$/.test(ext)) throw new KeyError(`blobPath(${kind}): bad extension ${JSON.stringify(ext)}`);
  return `${kind}/${hex.slice(0, 2)}/${hex.slice(2, 4)}/${hex}.${ext}`;
}
